import React, { useEffect, useState } from "react";
import { FileText, CheckCircle, XCircle, Clock, Eye } from "lucide-react";
import { useAuth } from "../../../context/AuthProvider";
import useSWR from "swr";
import { toast } from "react-toastify";
import {
  approveArchivalRequest,
  archivalApprovedAndReject,
  getVideo,
} from "../../../api/archivalReqestApis";
import { useNavigate } from "react-router-dom";
import logger from "../../../utils/logger";
import { formatDisplayDate, formatDisplayDateTime } from "../../../utils/Date";
import ArchivalRequestModal from "./ArchivalRequestModal";
import Pagination from "../../../utils/Pagination";

// Types
interface ArchivalRequest {
  _id: string;
  requestId: string;
  cnr: string;
  userId: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    role: string;
    username: string;
  };
  caseId: {
    hearingDate: string;
  };
  actionBy: {
    _id: string;
    username: string;
    firstName: string;
    lastName: string;
  };
  controller: {
    _id: string;
    firstName: string;
    lastName: string;
  };

  fileDetails: {
    status: string;
  };
  createdAt: string;
  status: "Approved" | "Pending" | "Rejected";
  reason?: string;
  actions?: {
    action: "Approved" | "Rejected";
    actionAt: string;
    actionBy: string;
  }[];
  actionDateTime?: string;
}

const ArchivalActivity: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // State with proper types
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [openingReject, setOpeningReject] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<ArchivalRequest | null>(null);
  const [showRejectReason, setShowRejectReason] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const roles = ["Advocate", "Individual", "Reader", "Judge"];
  const userType =
    typeof user?.role === "string" && roles.includes(user.role)
      ? "user"
      : "admins";

  const { data: archivalRequest, mutate } = useSWR<{
    data: ArchivalRequest[];
    total: number;
    pages: number;
    approvedCount: number;
    pendingCount: number;
    rejectedCount: number;
  }>(
    `/api/archival/${userType}/get-archival?page=${currentPage}&limit=10${statusFilter !== "all" ? `&status=${statusFilter}` : ""}`,
  );
  logger.log("checl coming data", archivalRequest);

  useEffect(() => {
    if (archivalRequest?.pages) {
      setTotalPages(archivalRequest.pages);
    }
  }, [archivalRequest]);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      Approved: { color: "bg-green-100 text-green-800", icon: CheckCircle },
      Pending: { color: "bg-yellow-100 text-yellow-800", icon: Clock },
      Rejected: { color: "bg-red-100 text-red-800", icon: XCircle },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] || statusConfig.Pending;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const handleArchival = async (
    request: ArchivalRequest,
    action: "Approve" | "Reject",
  ) => {
    try {
      if (action === "Reject") setRejecting(true);
      if (action === "Approve") setApproving(true);
      let payload = {
        cnr: request.cnr,
        action: action,
        _id: request._id,
        reason: action === "Reject" ? request.reason : undefined,
      };
      let res = await archivalApprovedAndReject(payload);
      let resData = res?.data;
      if (resData?.status) {
        mutate();
        setModalOpen(false);
        setSelectedRequest(null);
        toast.success(`Request ${action}ed successfully`);
      } else {
        toast.error(resData?.message || "Failed to process request");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to process request");
    } finally {
      setRejecting(false);
      setApproving(false);
    }
  };
  const handleApproveRequest = async (request: ArchivalRequest) => {
    try {
      setApproving(true);
      const res = await approveArchivalRequest({ id: request._id });
      logger.log(res?.data?.data, "check response data");

      if (res?.data?.status) {
        mutate();
        setModalOpen(false);
        setSelectedRequest(null);
        toast.success("Request approved successfully");
        setModalOpen(false);
        setSelectedRequest(null);
      } else {
        toast.error(res?.data?.message || "Failed to process request");
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to process request");
    } finally {
      setApproving(false);
    }
  };

  const handlePlayVideo = async (cnr: string, date: string) => {
    try {
      const payload = { cnr, hearingDate: date };
      let res = await getVideo(payload);
      logger.log("res", res);
      let resData = res?.data;
      if (resData?.status) {
        navigate("/recording", {
          state: {
            videoUrl: res.data.data,
            user,
            type: "Recorded Video",
          },
        });
      } else {
        toast.error("Failed to play video");
      }
    } catch (error: any) {
      logger.log("error>>>>", error);
      toast.error(error.message || "Failed to play video");
    }
  };

  const isRequestProcessed =
    selectedRequest?.status === "Rejected" ||
    selectedRequest?.status === "Approved";

  const isApproveDisabled = isRequestProcessed || approving;

  const isRejectDisabled = isRequestProcessed || openingReject;

  const getActionTime = (item: ArchivalRequest) => {
    if (item?.actions?.[0]?.actionAt) {
      return formatDisplayDateTime(item.actions[0].actionAt);
    }

    if (item?.actionDateTime) {
      return formatDisplayDateTime(item.actionDateTime);
    }

    return "—";
  };

  const closeModal = () => {
    setModalOpen(false);
    setShowRejectReason(false);
    setRejectReason("");
  };

  return (
    <div className="min-h-screen max-w-screen w-full ">
      <div className="w-full max-w-full lg:p-4 md:p-4 p-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold px-3 text-gray-900 ">
            Archival Requests
          </h1>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
          {/* TOTAL REQUESTS */}
          <div
            onClick={() => {
              setStatusFilter("all");
              setCurrentPage(1);
            }}
            className={`bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6 cursor-pointer transition-all ${
              statusFilter === "all"
                ? "ring-2 ring-blue-500"
                : "hover:shadow-md"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">
                  Total Requests
                </p>
                <p className="text-lg sm:text-3xl font-bold text-gray-700">
                  {archivalRequest?.total || 0}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                <FileText className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* APPROVED */}
          <div
            onClick={() => {
              setStatusFilter("Approved");
              setCurrentPage(1);
            }}
            className={`bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6 cursor-pointer transition-all ${
              statusFilter === "Approved"
                ? "ring-2 ring-green-500"
                : "hover:shadow-md"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Approved</p>
                <p className="text-3xl font-bold text-green-600">
                  {archivalRequest?.approvedCount || 0}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
                <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* PENDING */}
          <div
            onClick={() => {
              setStatusFilter("Pending");
              setCurrentPage(1);
            }}
            className={`bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6 cursor-pointer transition-all ${
              statusFilter === "Pending"
                ? "ring-2 ring-yellow-500"
                : "hover:shadow-md"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-3xl font-bold text-yellow-600">
                  {archivalRequest?.pendingCount || 0}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-yellow-100 rounded-lg">
                <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-yellow-600" />
              </div>
            </div>
          </div>

          {/* REJECTED */}
          <div
            onClick={() => {
              setStatusFilter("Rejected");
              setCurrentPage(1);
            }}
            className={`bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-6 cursor-pointer transition-all ${
              statusFilter === "Rejected"
                ? "ring-2 ring-red-500"
                : "hover:shadow-md"
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Rejected</p>
                <p className="text-3xl font-bold text-red-600">
                  {archivalRequest?.rejectedCount || 0}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-red-100 rounded-lg">
                <XCircle className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white w-full rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="w-full overflow-x-auto">
            <table className="table w-full">
              <thead className="bg-gray-50 border border-gray-100">
                <tr>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    S. No
                  </th>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    Request ID
                  </th>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    User
                  </th>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    Request Date
                  </th>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    Status
                  </th>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    Action Time
                  </th>
                  <th className="text-left p-2 text-gray-800 font-medium">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {archivalRequest && archivalRequest?.data?.length > 0 ? (
                  archivalRequest.data.map(
                    (item: ArchivalRequest, idx: number) => (
                      <tr
                        key={idx}
                        className={`border-b border-gray-100 transition-colors ${
                          idx % 2 === 0 ? "bg-white" : "bg-blue-50"
                        } hover:bg-gray-50`}
                      >
                        <td className="p-2 text-gray-700 font-normal">
                          {(currentPage - 1) * 10 + idx + 1}
                        </td>

                        <td className="p-2 text-gray-700 font-normal">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {item.requestId || "—"}
                          </span>
                        </td>

                        <td className="p-2 text-gray-700 font-normal">
                          {item?.userId?.firstName && item?.userId?.lastName
                            ? `${item.userId.firstName} ${item.userId.lastName}`
                            : "—"}
                        </td>

                        <td className="p-2 text-gray-700 font-normal">
                          {formatDisplayDateTime(item.createdAt)}
                        </td>

                        <td className="p-2">{getStatusBadge(item.status)}</td>

                        <td className="p-2 text-gray-700 font-normal">
                          {getActionTime(item)}
                        </td>

                        <td className="p-2">
                          <div className="flex space-x-1">
                            <button
                              onClick={() => {
                                setSelectedRequest(item);
                                setModalOpen(true);
                              }}
                              className="text-blue-500 hover:text-gray-800 p-2 rounded-lg hover:bg-white/10 transition-colors"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>

                            {user?.role !== "Controller" &&
                              item.status === "Approved" &&
                              item?.fileDetails?.status !== "deleted" && (
                                <button
                                  onClick={() =>
                                    handlePlayVideo(
                                      item.cnr,
                                      item?.caseId?.hearingDate,
                                    )
                                  }
                                  className="text-white bg-blue-600 hover:bg-blue-700 p-2 rounded-lg transition-colors"
                                >
                                  Play
                                </button>
                              )}
                          </div>
                        </td>
                      </tr>
                    ),
                  )
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-16 text-center text-gray-500"
                    >
                      <div className="flex flex-col items-center justify-center space-y-3">
                        <FileText className="h-12 w-12 text-gray-300" />
                        <h3 className="text-lg font-medium text-gray-600 mb-0">
                          No Records Found
                        </h3>
                        <p className="text-xs text-gray-400">
                          Try adjusting your filters to see more results
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {/* Modal */}
      {modalOpen && selectedRequest && (
        <ArchivalRequestModal
          modalOpen={modalOpen}
          selectedRequest={selectedRequest}
          closeModal={closeModal}
          getStatusBadge={getStatusBadge}
          formatDisplayDate={formatDisplayDate}
          formatDisplayDateTime={formatDisplayDateTime}
          user={user}
          showRejectReason={showRejectReason}
          setShowRejectReason={setShowRejectReason}
          rejectReason={rejectReason}
          setRejectReason={setRejectReason}
          approving={approving}
          rejecting={rejecting}
          openingReject={openingReject}
          isApproveDisabled={isApproveDisabled}
          isRejectDisabled={isRejectDisabled}
          handleApproveRequest={handleApproveRequest}
          handleArchival={handleArchival}
          setOpeningReject={setOpeningReject}
        />
      )}
    </div>
  );
};

export default ArchivalActivity;
